import { inject } from "vue"
import { computed } from "vue"
import { Ref } from "vue"
import { unref } from "vue"
import { toRef } from "vue"
import { provide } from "vue"
import { MaybeRef } from "vue"
import { useRoute } from "vue-router"

const INJECTION_SYMBOL = Symbol('ModalData')

export type ModalDataRaw = {
    id: string
    showCloseButton: boolean
    transitionAppear?: boolean
    // default value: modal-xl. set to null to disable
    defaultModalSizeClass?: string | null
}

export type ModalData = {
    id: MaybeRef<string>
    labelId: MaybeRef<string>
    showCloseButton: MaybeRef<boolean>
    transitionAppear: MaybeRef<boolean>
    // default value: modal-xl. set to null to disable
    defaultModalSizeClass: MaybeRef<string | null | undefined>
    _raw: ModalDataRaw
}

export function provideModalData(props: MaybeRef<ModalDataRaw>) {
    const route = useRoute()
    const data = {
        id: computed(() => unref(props).id),
        labelId: computed(() => `${unref(props).id}-label`),
        showCloseButton: computed(() => unref(props).showCloseButton),
        transitionAppear: computed(() => unref(props).transitionAppear),
        defaultModalSizeClass: computed(() => {
            const _route = unref(route)
            const { meta } = _route ?? {}
            const _default = meta?.defaultModalClass
            const _explicit = unref(props).defaultModalSizeClass
            // disable with a null value, otherwise use (_explicit ?? _default)
            const _value = _explicit !== null ? (_explicit ?? _default) : null
            return _value
        }),
        _raw: props,
    }
    provide(INJECTION_SYMBOL, data)
    provide('wrapper', 'modal')
    return data
}

export function useModalData() {
    const result = inject<ModalData | undefined>(INJECTION_SYMBOL)

    if (!result) {
        throw new Error("Sanity check failed: no ModalData.")
    }

    return result
}

export function emitClose(event: Event) {
    const target = event.target! as HTMLElement

    const _event = new CustomEvent('close', { bubbles: true })
    target.dispatchEvent(_event)
}
